import { Badge, Button, EmptyState, PageLayout, Select, Tabs, useToast } from '@orbind/ui'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import {
  ANALYTICS_PERIODS,
  DEFAULT_ANALYTICS_PERIOD,
  getAnalytics,
  type AnalyticsPeriodId,
  type BarSeries,
  type LineSeries,
  type MetricCard,
  type MetricTone,
  type PortfolioProgressRow,
  type SlaByPriorityRow,
} from '../mocks/analytics.ts'
import './analytics.css'

type AnalyticsTab = 'flow' | 'demands' | 'portfolio'

const TABS: readonly { id: AnalyticsTab; label: string; icon: string; path: string }[] = [
  { id: 'flow', label: 'Fluxo', icon: 'fas fa-stream', path: '/analytics/flow' },
  { id: 'demands', label: 'Demandas', icon: 'fas fa-inbox', path: '/analytics/demands' },
  { id: 'portfolio', label: 'Portfólio', icon: 'fas fa-layer-group', path: '/analytics/portfolio' },
]

/** Chave de periodo guardada por usuario no navegador. */
const PERIOD_KEY = 'orbind.analytics.period'

function readStoredPeriod(): AnalyticsPeriodId {
  const stored = localStorage.getItem(PERIOD_KEY)
  const known = ANALYTICS_PERIODS.some((option) => option.value === stored)
  return known ? (stored as AnalyticsPeriodId) : DEFAULT_ANALYTICS_PERIOD
}

/**
 * MET-01..04 — Métricas.
 *
 * Painel com cartoes de indicador e graficos desenhados em SVG a mao (sem
 * biblioteca externa). Todo grafico imprime os numeros como texto: cor e
 * reforco, nunca o unico portador da informacao.
 *
 * DADOS MOCADOS: `src/mocks/analytics.ts`. Nao ha rota de API de metricas.
 */
export function AnalyticsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()

  const tab: AnalyticsTab = TABS.find((entry) => location.pathname.startsWith(entry.path))?.id ?? 'flow'
  // O periodo escolhido acompanha o usuario entre as abas e entre as sessoes.
  const [period, setPeriod] = useState<AnalyticsPeriodId>(readStoredPeriod)
  const snapshot = getAnalytics(period)

  const changePeriod = (next: AnalyticsPeriodId) => {
    localStorage.setItem(PERIOD_KEY, next)
    setPeriod(next)
  }

  return (
    <PageLayout
      title="Métricas"
      subtitle={
        snapshot
          ? `${snapshot.periodLabel} · ${snapshot.generatedAtLabel}`
          : 'Indicadores de fluxo, demandas e portfólio'
      }
      actions={
        <Button
          icon="fas fa-file-export"
          onClick={() =>
            toast.push({ tone: 'info', message: 'Exportação indisponível: as métricas ainda não têm API.' })
          }
        >
          Exportar
        </Button>
      }
      tabs={
        <Tabs
          items={TABS.map((entry) => ({ id: entry.id, label: entry.label, icon: entry.icon }))}
          value={tab}
          onChange={(next) => {
            const target = TABS.find((entry) => entry.id === next)
            if (target) navigate(target.path)
          }}
        />
      }
      filters={
        <div className="mx-filters">
          <span className="mx-filters__label" id="mx-period-label">
            Período
          </span>
          <div style={{ width: 220 }}>
            <Select
              aria-labelledby="mx-period-label"
              value={period}
              options={ANALYTICS_PERIODS.map((option) => ({ value: option.value, label: option.label }))}
              onChange={changePeriod}
            />
          </div>
        </div>
      }
    >
      {snapshot === null ? (
        <EmptyState
          icon="fas fa-chart-bar"
          title="Ainda não há números fechados para este período."
          description="O consolidado de métricas roda uma vez por semana, então a janela de 7 dias ainda não tem dado apurado. Escolha um período maior para ver os indicadores."
          action={
            <Button variant="primary" icon="fas fa-calendar-alt" onClick={() => changePeriod('30d')}>
              Ver os últimos 30 dias
            </Button>
          }
        />
      ) : tab === 'flow' ? (
        <>
          <MetricCards cards={snapshot.flow.cards} label="Indicadores de fluxo" />
          <div className="mx-grid">
            <LineChart series={snapshot.flow.leadTime} />
            <BarChart series={snapshot.flow.throughput} />
            <BarChart series={snapshot.flow.cycleTimeByType} />
            <BarChart series={snapshot.flow.wipByStatus} />
          </div>
        </>
      ) : tab === 'demands' ? (
        <>
          <MetricCards cards={snapshot.demands.cards} label="Indicadores de demandas" />
          <div className="mx-grid">
            <LineChart series={snapshot.demands.slaCompliance} />
            <BarChart series={snapshot.demands.byChannel} />
            <BarChart series={snapshot.demands.byStage} />
          </div>
          <SlaTable rows={snapshot.demands.slaByPriority} />
        </>
      ) : (
        <>
          <MetricCards cards={snapshot.portfolio.cards} label="Indicadores de portfólio" />
          <ProgressList rows={snapshot.portfolio.progressBySolution} />
          <div className="mx-grid">
            <LineChart series={snapshot.portfolio.deliveryTrend} />
            <BarChart series={snapshot.portfolio.itemsByLevel} />
          </div>
        </>
      )}
    </PageLayout>
  )
}

// ------------------------------------------------------- cartoes de KPI ---

function MetricCards({ cards, label }: { cards: readonly MetricCard[]; label: string }) {
  return (
    <section className="mx-cards" aria-label={label}>
      {cards.map((card) => (
        <article key={card.id} className={`mx-card mx-card--${card.tone}`}>
          <h3 className="mx-card__label">{card.label}</h3>
          <p className="mx-card__value">
            {card.value}
            <span className="mx-card__unit">{card.unit}</span>
          </p>
          {card.delta ? (
            <p className={`mx-delta mx-delta--${card.delta.isGood ? 'good' : 'bad'}`}>
              <i
                className={
                  card.delta.direction === 'up'
                    ? 'fas fa-arrow-up'
                    : card.delta.direction === 'down'
                      ? 'fas fa-arrow-down'
                      : 'fas fa-arrow-right'
                }
                aria-hidden
              />
              {card.delta.label}
              <span className="mx-delta__reading">{card.delta.isGood ? '(melhorou)' : '(piorou)'}</span>
            </p>
          ) : (
            <p className="mx-delta mx-delta--none">Sem base de comparação no período</p>
          )}
          <p className="mx-card__hint">{card.hint}</p>
        </article>
      ))}
    </section>
  )
}

// --------------------------------------------------------- graficos SVG ---

/** Cor de preenchimento por tom. A cor NUNCA carrega sozinha a informacao:
 *  todo valor e impresso como texto e cada tom entra na legenda com rotulo. */
const TONE_FILL: Record<MetricTone, string> = {
  neutral: 'var(--ob-primary)',
  success: 'var(--ob-success)',
  warning: 'var(--ob-warning)',
  danger: 'var(--ob-danger)',
}

const TONE_LABEL: Record<MetricTone, string> = {
  neutral: 'Dentro do esperado',
  success: 'Melhor resultado',
  warning: 'Merece atenção',
  danger: 'Fora do aceitável',
}

const nf = (value: number) => value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })

/** Teto "redondo" do eixo Y para os rotulos nao sairem quebrados. */
function niceCeiling(max: number): number {
  if (max <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(max))
  const normalized = max / magnitude
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
  return step * magnitude
}

/** Quebra o rotulo do eixo X em ate duas linhas para nao cortar palavra. */
function wrapLabel(label: string, maxChars = 13): string[] {
  if (label.length <= maxChars) return [label]
  const words = label.split(' ')
  const lines: string[] = []
  let current = ''
  for (const word of words) {
    const candidate = current === '' ? word : `${current} ${word}`
    if (candidate.length > maxChars && current !== '') {
      lines.push(current)
      current = word
    } else {
      current = candidate
    }
  }
  if (current !== '') lines.push(current)
  if (lines.length <= 2) return lines
  const [first, second] = lines
  return [first ?? label, `${second ?? ''}…`]
}

const PAD_L = 48
const PAD_T = 26
const PLOT_H = 158
const LABEL_H = 44

/**
 * Grafico de barras verticais em SVG puro.
 *
 * Cada barra imprime o proprio valor acima dela e o rotulo abaixo; o eixo Y tem
 * quatro linhas de grade rotuladas. Nao usa biblioteca externa.
 */
function BarChart({ series }: { series: BarSeries }) {
  const barWidth = 52
  const gap = 20
  const padRight = 16
  const count = series.bars.length
  const width = PAD_L + padRight + count * barWidth + Math.max(0, count - 1) * gap
  const height = PAD_T + PLOT_H + LABEL_H
  const ceiling = niceCeiling(series.bars.reduce((max, bar) => Math.max(max, bar.value), 0))
  const gridValues = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ceiling * ratio)
  const tonesUsed = [...new Set(series.bars.map((bar) => bar.tone ?? 'neutral'))]

  const summary = series.bars.map((bar) => `${bar.label}: ${nf(bar.value)} ${series.unit}`).join('; ')

  return (
    <figure className="mx-chart">
      <figcaption className="mx-chart__caption">
        <h3 className="mx-chart__title">{series.title}</h3>
        <p className="mx-chart__text">{series.caption}</p>
      </figcaption>

      <div className="mx-chart__canvas">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          style={{ maxWidth: width, height: 'auto' }}
          role="img"
          aria-label={`${series.title}. ${summary}.`}
        >
          <title>{series.title}</title>
          <desc>{summary}</desc>

          {gridValues.map((value) => {
            const y = PAD_T + PLOT_H - (value / ceiling) * PLOT_H
            return (
              <g key={value}>
                <line
                  x1={PAD_L}
                  x2={width - padRight}
                  y1={y}
                  y2={y}
                  stroke="var(--ob-border)"
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                <text x={PAD_L - 8} y={y + 4} textAnchor="end" className="mx-svg__axis">
                  {nf(value)}
                </text>
              </g>
            )
          })}

          {series.bars.map((bar, index) => {
            const x = PAD_L + index * (barWidth + gap)
            const barHeight = Math.max(2, (bar.value / ceiling) * PLOT_H)
            const y = PAD_T + PLOT_H - barHeight
            const lines = wrapLabel(bar.label)
            return (
              <g key={bar.label}>
                <rect x={x} y={y} width={barWidth} height={barHeight} rx={5} fill={TONE_FILL[bar.tone ?? 'neutral']} />
                <text x={x + barWidth / 2} y={y - 7} textAnchor="middle" className="mx-svg__value">
                  {nf(bar.value)}
                </text>
                {lines.map((line, lineIndex) => (
                  <text
                    key={line}
                    x={x + barWidth / 2}
                    y={PAD_T + PLOT_H + 17 + lineIndex * 13}
                    textAnchor="middle"
                    className="mx-svg__label"
                  >
                    {line}
                  </text>
                ))}
              </g>
            )
          })}
        </svg>
      </div>

      <p className="mx-chart__unit">Valores em {series.unit}.</p>
      {tonesUsed.length > 1 ? <ToneLegend tones={tonesUsed} /> : null}
    </figure>
  )
}

/**
 * Grafico de linha em SVG puro.
 *
 * Marca cada ponto com o valor em texto e, quando existe meta, desenha a linha
 * de referencia tracejada com o rotulo escrito ao lado.
 */
function LineChart({ series }: { series: LineSeries }) {
  const step = 92
  const padRight = 74
  const count = series.points.length
  const width = PAD_L + padRight + Math.max(1, count - 1) * step
  const height = PAD_T + PLOT_H + LABEL_H
  const rawMax = series.points.reduce((max, point) => Math.max(max, point.value), series.target?.value ?? 0)
  const ceiling = niceCeiling(rawMax)
  const gridValues = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ceiling * ratio)

  const xOf = (index: number) => PAD_L + index * step
  const yOf = (value: number) => PAD_T + PLOT_H - (value / ceiling) * PLOT_H

  const path = series.points.map((point, index) => `${xOf(index)},${yOf(point.value)}`).join(' ')
  const areaPath =
    count > 0 ? `${PAD_L},${PAD_T + PLOT_H} ${path} ${xOf(count - 1)},${PAD_T + PLOT_H}` : ''
  const summary = series.points.map((point) => `${point.label}: ${nf(point.value)}${series.unit}`).join('; ')

  return (
    <figure className="mx-chart">
      <figcaption className="mx-chart__caption">
        <h3 className="mx-chart__title">{series.title}</h3>
        <p className="mx-chart__text">{series.caption}</p>
      </figcaption>

      <div className="mx-chart__canvas">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          style={{ maxWidth: width, height: 'auto' }}
          role="img"
          aria-label={`${series.title}. ${summary}.${series.target ? ` ${series.target.label}.` : ''}`}
        >
          <title>{series.title}</title>
          <desc>{summary}</desc>

          {gridValues.map((value) => {
            const y = yOf(value)
            return (
              <g key={value}>
                <line
                  x1={PAD_L}
                  x2={width - padRight + 12}
                  y1={y}
                  y2={y}
                  stroke="var(--ob-border)"
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                <text x={PAD_L - 8} y={y + 4} textAnchor="end" className="mx-svg__axis">
                  {nf(value)}
                </text>
              </g>
            )
          })}

          {series.target ? (
            <g>
              <line
                x1={PAD_L}
                x2={width - padRight + 12}
                y1={yOf(series.target.value)}
                y2={yOf(series.target.value)}
                stroke="var(--ob-success)"
                strokeWidth={1.5}
                strokeDasharray="5 4"
              />
              <text
                x={width - padRight + 18}
                y={yOf(series.target.value) + 4}
                textAnchor="start"
                className="mx-svg__target"
              >
                {series.target.label}
              </text>
            </g>
          ) : null}

          {areaPath ? <polygon points={areaPath} fill="var(--ob-primary-tint)" opacity={0.55} /> : null}
          <polyline
            points={path}
            fill="none"
            stroke="var(--ob-primary)"
            strokeWidth={2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {series.points.map((point, index) => (
            <g key={point.label}>
              <circle cx={xOf(index)} cy={yOf(point.value)} r={4.5} fill="var(--ob-surface)" stroke="var(--ob-primary)" strokeWidth={2.5} />
              <text x={xOf(index)} y={yOf(point.value) - 12} textAnchor="middle" className="mx-svg__value">
                {nf(point.value)}
                {series.unit === '%' ? '%' : ''}
              </text>
              <text x={xOf(index)} y={PAD_T + PLOT_H + 20} textAnchor="middle" className="mx-svg__label">
                {point.label}
              </text>
            </g>
          ))}
        </svg>
      </div>

      <p className="mx-chart__unit">
        Valores em {series.unit === '%' ? 'percentual' : series.unit === 'd' ? 'dias' : series.unit}.
      </p>
    </figure>
  )
}

/** Legenda textual dos tons usados no grafico (cor + rotulo escrito). */
function ToneLegend({ tones }: { tones: readonly MetricTone[] }) {
  return (
    <ul className="mx-legend">
      {tones.map((tone) => (
        <li key={tone} className="mx-legend__item">
          <span className="mx-legend__swatch" style={{ background: TONE_FILL[tone] }} aria-hidden />
          {TONE_LABEL[tone]}
        </li>
      ))}
    </ul>
  )
}

// ------------------------------------------------------------- tabelas ---

function SlaTable({ rows }: { rows: readonly SlaByPriorityRow[] }) {
  return (
    <section className="mx-block" aria-label="Cumprimento de SLA por prioridade">
      <h3 className="mx-block__title">Cumprimento de SLA por prioridade</h3>
      <p className="mx-block__text">
        Cada prioridade tem um prazo de resposta próprio. A coluna de cumprimento mostra o percentual atendido dentro do
        prazo.
      </p>
      <div className="mx-tablewrap">
        <table className="mx-table">
          <thead>
            <tr>
              <th scope="col">Prioridade</th>
              <th scope="col">Meta</th>
              <th scope="col" className="mx-table__num">
                Demandas
              </th>
              <th scope="col" className="mx-table__num">
                No prazo
              </th>
              <th scope="col" className="mx-table__num">
                Estouradas
              </th>
              <th scope="col" className="mx-table__num">
                Cumprimento
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const rate = row.total === 0 ? 0 : Math.round((row.onTime / row.total) * 100)
              const tone = rate >= 95 ? 'success' : rate >= 85 ? 'warning' : 'danger'
              return (
                <tr key={row.priority}>
                  <th scope="row">{row.priority}</th>
                  <td>{row.targetLabel}</td>
                  <td className="mx-table__num">{row.total.toLocaleString('pt-BR')}</td>
                  <td className="mx-table__num">{row.onTime.toLocaleString('pt-BR')}</td>
                  <td className="mx-table__num">{row.breached.toLocaleString('pt-BR')}</td>
                  <td className="mx-table__num">
                    <Badge
                      tone={tone}
                      icon={
                        tone === 'success'
                          ? 'fas fa-check-circle'
                          : tone === 'warning'
                            ? 'fas fa-exclamation-circle'
                            : 'fas fa-times-circle'
                      }
                    >
                      {rate}%
                    </Badge>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function ProgressList({ rows }: { rows: readonly PortfolioProgressRow[] }) {
  return (
    <section className="mx-block" aria-label="Progresso por Solução">
      <h3 className="mx-block__title">Progresso por Solução</h3>
      <p className="mx-block__text">
        Itens de Trabalho concluídos sobre o total planejado em cada Solução do portfólio.
      </p>
      <ul className="mx-progress">
        {rows.map((row) => {
          const percent = row.total === 0 ? 0 : Math.round((row.done / row.total) * 100)
          return (
            <li key={row.solution} className="mx-progress__row">
              <div className="mx-progress__head">
                <span className="mx-progress__name">{row.solution}</span>
                <span className="mx-progress__owner">{row.owner}</span>
                {row.blocked > 0 ? (
                  <Badge tone="danger" icon="fas fa-ban">
                    {row.blocked} {row.blocked === 1 ? 'item bloqueado' : 'itens bloqueados'}
                  </Badge>
                ) : null}
              </div>
              <div className="mx-progress__barwrap">
                <div
                  className="mx-progress__bar"
                  role="img"
                  aria-label={`${percent}% concluído: ${row.done} de ${row.total} Itens de Trabalho.`}
                >
                  <span className="mx-progress__fill" style={{ width: `${percent}%` }} />
                </div>
                <span className="mx-progress__numbers">
                  {row.done.toLocaleString('pt-BR')} de {row.total.toLocaleString('pt-BR')} ({percent}%)
                </span>
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
